<template>
  <span>
    <el-button v-if="showSubmit" type="primary" size="small" @click="submit">提交审批</el-button>
    <el-button v-if="showWfApprove" type="primary" size="small" @click="pass">审批通过</el-button>
    <el-button v-if="showWfReject" type="primary" size="small" @click="reject">驳回</el-button>
    <el-button v-if="showDirect" type="primary" size="small" @click="$emit('approve')">审核</el-button>
    <el-button v-if="showUnapprove" type="primary" size="small" :disabled="unapproveDisabled" @click="$emit('unapprove')">弃审</el-button>
    <el-button v-if="showTrail" type="primary" size="small" @click="trail = true">审批轨迹</el-button>
    <span v-if="hint" class="wf-hint">{{ hint }}</span>
    <workflow-trail-dialog :visible.sync="trail" :workflow-instance="instance" :tasks="tasks" />
  </span>
</template>

<script>
import WorkflowTrailDialog from './WorkflowTrailDialog.vue'
import request from '../api/request'
import { fetchPerms } from '../api/auth'
import { canShowDirectApproveButton, normalizeWorkflowDocUi, shouldShowWorkflowTrail } from '../utils/workflow'

/** 单据查看页上的多级审批按钮，显隐规则与原系统单据弹框一致。 */
export default {
  name: 'WorkflowDocButtons',
  components: { WorkflowTrailDialog },
  props: {
    bizType: { type: String, required: true },
    bizId: { type: String, default: '' },
    perm: { type: String, required: true },
    approveStatus: { type: [String, Number], default: '0' },
    docLabel: { type: String, default: '单据' },
    unapproveDisabled: { type: Boolean, default: false },
    allowUnapprove: { type: Boolean, default: true }
  },
  data() {
    return { pack: null, ready: false, trail: false, permCodes: [] }
  },
  created() {
    fetchPerms().then(res => { this.permCodes = res.data || [] }).catch(() => {})
  },
  computed: {
    ui() {
      return normalizeWorkflowDocUi(this.pack && this.pack.workflowDocUi) || {}
    },
    task() {
      return (this.pack && this.pack.myPendingTask) || null
    },
    instance() {
      return (this.pack && this.pack.workflowInstance) || null
    },
    tasks() {
      return (this.instance && this.instance.tasks) || []
    },
    showSubmit() {
      if (!this.ready || !this.ui.submitAllowed) return false
      return this.isAuth(this.perm + ':submit') || this.isAuth(this.perm + ':save') || this.isAuth(this.perm + ':update')
    },
    showWfApprove() {
      return this.ready && this.isAuth('workflow:task:approve') && this.task && this.task.taskId
    },
    showWfReject() {
      return this.showWfApprove && this.isAuth('workflow:task:reject')
    },
    showDirect() {
      if (!this.ready || String(this.approveStatus) === '2') return false
      if (!canShowDirectApproveButton(this.pack && this.pack.workflowDocUi)) return false
      if (this.isAuth(this.perm + ':approve')) return true
      const ui = this.ui
      return !!(ui.workflowEnabled && !ui.definitionMatched && ui.directApproveAllowed) && (this.isAuth(this.perm + ':save') || this.isAuth(this.perm + ':update'))
    },
    showUnapprove() {
      return this.allowUnapprove && this.ready && this.isAuth(this.perm + ':unapprove') && String(this.approveStatus) === '2'
    },
    hint() {
      const ui = this.ui
      if (!this.ready || !ui.workflowEnabled || String(this.approveStatus) === '2') return ''
      if (ui.inApproval) {
        const uid = (this.$store.state.user || {}).id
        const inst = this.instance
        if (!(this.task && this.task.taskId) && inst && uid && String(inst.submitBy) === String(uid)) {
          return '当前步骤需由其他审批人处理（不允许提交人自审），请等待待办人审批。'
        }
        return ''
      }
      if (!ui.definitionMatched && ui.submitAllowed) return '已启用多级审批，尚未配置审批流程。请点击「提交审批」查看配置指引。'
      if (!ui.definitionMatched && ui.directApproveAllowed) return '已启用多级审批，尚未配置匹配的审批流程，可直接审核或联系管理员配置流程。'
      if (!ui.definitionMatched && !ui.directApproveAllowed && !ui.submitAllowed) return '已启用多级审批，请先在「审批流程定义」中配置' + this.docLabel + '流程后再提交。'
      return ''
    },
    showTrail() {
      return shouldShowWorkflowTrail({
        inViewMode: true,
        workflowInstance: this.instance,
        workflowDocUi: this.ui,
        workflowDocUiReady: this.ready
      })
    }
  },
  watch: {
    bizId: { immediate: true, handler() { this.load() } },
    approveStatus() { this.load() }
  },
  methods: {
    isAuth(perm) {
      const user = this.$store.state.user || {}
      const codes = Array.isArray(user.roleCodes) ? user.roleCodes : []
      const role = String(user.roleCode || '')
      if (role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN' || codes.indexOf('TENANT_ADMIN') >= 0 || codes.indexOf('PLATFORM_ADMIN') >= 0) return true
      return this.permCodes.indexOf(perm) >= 0
    },
    load() {
      if (!this.bizId) {
        this.pack = null
        this.ready = false
        return
      }
      request.get('/workflow/document', { params: { bizType: this.bizType, bizId: this.bizId } }).then(res => {
        this.pack = res.data || {}
        this.ready = true
      }).catch(() => {
        this.pack = { workflowDocUi: { workflowEnabled: false, definitionMatched: false, inApproval: false, directApproveAllowed: true, submitAllowed: false } }
        this.ready = true
      })
    },
    submit() {
      const ui = this.ui
      if (ui.workflowEnabled && !ui.definitionMatched) {
        this.$alert('尚未配置' + this.docLabel + '审批流程，请先在「审批流程定义」中新增并启用流程（至少一个审批步骤）后再提交。', '未配置审批流程', { type: 'warning', confirmButtonText: '我知道了' })
        return
      }
      this.$confirm('提交后将进入审批流程，期间不可修改，是否继续?', '提示', { type: 'warning' }).then(() => {
        request.post('/workflow/submit', { bizType: this.bizType, bizId: this.bizId }).then(() => {
          this.$message.success('提交成功')
          this.load()
          this.$emit('done')
        }).catch(() => {})
      }).catch(() => {})
    },
    pass() {
      const task = this.task
      if (!task || !task.taskId) return
      this.$prompt('审批意见（可选）', '审批通过', { inputType: 'textarea', confirmButtonText: '通过' }).then(({ value }) => {
        request.post('/workflow/task/approve', { taskId: task.taskId, comment: value || '' }).then(() => {
          this.$message.success('审批通过')
          this.load()
          this.$emit('done')
        }).catch(() => {})
      }).catch(() => {})
    },
    reject() {
      const task = this.task
      if (!task || !task.taskId) return
      this.$prompt('请输入驳回原因', '审批驳回', {
        inputType: 'textarea',
        confirmButtonText: '驳回',
        inputValidator: v => !!(v && String(v).trim()) || '驳回原因不能为空'
      }).then(({ value }) => {
        request.post('/workflow/task/reject', { taskId: task.taskId, comment: String(value).trim() }).then(() => {
          this.$message.success('已驳回')
          this.load()
          this.$emit('done')
        }).catch(() => {})
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.wf-hint { display: inline-block; margin-left: 8px; color: #e6a23c; font-size: 12px; line-height: 1.4; max-width: 420px; white-space: normal; vertical-align: middle; }
</style>
